<template>
  <div>
    <modal :value="showModal" :max-width="maxWidth" @input="onClose">
      <template #title>Upload References</template>
      <template #content>
        <div v-if="awaitingFileSelection" class="pa-3">
          To get started, upload the files containing your considered
          references. We currently support the
          <strong class="font-weight-bold">RIS</strong> and
          <strong>EndNote XML</strong> formats.
        </div>
        <div
          v-else-if="awaitingSubmission"
          class="line-height-150 text-body-2 font-weight-light pa-2 mb-8"
        >
          <p>
            The reference files you've selected are listed below. By
            default, they are set to
            <v-chip small>
              <v-icon small left class="material-icons-outlined">
                inbox
              </v-icon>
              Unscreened</v-chip
            >.
          </p>
          <p class="mb-0">
            If any of the files contain pre-screened references, you
            can change their status to either
            <v-chip small color="success">
              <v-icon small left class="material-icons-outlined">
                done_all
              </v-icon>
              Relevant
            </v-chip>
            or
            <v-chip small color="error">
              <v-icon small left class="material-icons-outlined">
                remove_circle
              </v-icon>
              Not Relevant
            </v-chip>
            to reflect this, which will set all references within
            those files to reflect that status.
          </p>
        </div>
        <div
          v-else-if="processing"
          class="py-2 mb-4 line-height-150 text-body-2"
        >
          <template v-if="areUploadsInProgress">
            Your files are being uploaded. Until this process is
            complete, please keep this window open and do not refresh
            or close your browser.
          </template>
          <template v-else>
            <p>
              All files have been uploaded and are now being
              processed. You can now safely close this window and your
              uploaded files will be processed in the background.
            </p>
            <p>
              The references in your files will appear incrementally
              as the files are processed and you can commence
              screening at any time.
            </p>
          </template>
        </div>

        <template v-if="awaitingFileSelection || awaitingSubmission">
          <h4>References</h4>
          <reference-file-selector
            v-model="referenceFileSelectorModel"
            class="file-selector mt-1"
            :class="referenceFileSelectorClasses"
            @set-label="onSetLabel($event.file, $event.label)"
            @set-source="onSetSource($event.file, $event.source)"
          />
        </template>
        <div v-else class="mt-1">
          <scroll-view class="file-list">
            <reference-upload-list-item
              v-for="upload in uploadingFiles"
              :key="upload.id"
              :file="upload.file"
              :progress="upload.progress * 100"
              :error="errors[upload.id]"
              @cancel="cancelUpload(upload)"
            />
            <v-expansion-panels accordion>
              <reference-processing-list-item
                v-for="referenceFile in referenceFiles"
                :key="referenceFile.key"
                :reference-file="referenceFile"
              />
            </v-expansion-panels>
          </scroll-view>
          <div v-if="allUploadsFailed">
            <div class="caption px-3 pb-0">
              <v-icon color="error" small>error</v-icon>
              Please resolve the errors and try again.
            </div>
          </div>
        </div>
        <div v-if="completeWithDuplicates" class="text-center mt-4">
          <p>
            All file processing has completed. Some files contained
            duplicates that we recommend you review and confirm.
          </p>
          <p class="mb-0">
            Would you like to review the duplicates now?
          </p>
        </div>
      </template>
      <template #actions>
        <action-button
          v-if="allUploadsFailed"
          class="mr-2"
          @click="cancelUploads"
        >
          Go back
        </action-button>
        <template v-if="completeWithDuplicates">
          <action-button class="mr-2" @click="onClose">
            Not Now
          </action-button>
          <action-button color="primary" @click="onResolveDuplicates">
            <v-icon left>rule</v-icon>
            Resolve duplicates
          </action-button>
          <v-spacer />
        </template>
        <action-button v-else class="mr-2" @click="onClose">
          Close
        </action-button>
        <action-button
          v-if="awaitingFileSelection || awaitingSubmission"
          color="primary"
          :disabled="awaitingFileSelection"
          @click="onUpload"
        >
          <v-icon class="material-icons-outlined" left>
            cloud_upload
          </v-icon>
          Upload
        </action-button>
      </template>
    </modal>
    <upload-progress-toast
      v-model="toastModel"
      :uploading-count="toastUploadingCount"
      :processing-count="toastProcessingCount"
      :complete-count="toastCompleteCount"
      @view="onViewProgress"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import Modal from '@/components/Common/Modal';
import ActionButton from '@/components/Common/ActionButton';
import ScrollView from '@/components/Common/ScrollView';
import ReferenceFileSelector from './ReferenceFileSelector';
import ReferenceUploadListItem from './ReferenceUploadListItem';
import ReferenceProcessingListItem from './ReferenceProcessingListItem';
import UploadProgressToast from './UploadProgressToast';
import { Review } from '@/models';
import { mapActions, mapGetters } from 'vuex';
import { v4 as createUUID } from 'uuid';

const ModalState = Object.freeze({
  AWAITING_FILE_SELECTION: 'AWAITING_FILE_SELECTION',
  AWAITING_SUBMISSION: 'AWAITING_SUBMISSION',
  PROCESSING: 'PROCESSING',
  COMPLETE: 'COMPLETE',
  COMPLETE_WITH_DUPLICATES: 'COMPLETE_WITH_DUPLICATES',
});

export default {
  name: 'UploadReferencesModal',
  components: {
    Modal,
    ActionButton,
    ReferenceFileSelector,
    ReferenceUploadListItem,
    ReferenceProcessingListItem,
    ScrollView,
    UploadProgressToast,
  },
  props: {
    review: {
      type: Review,
      default: null,
    },
  },
  data: () => ({
    fileLabels: {},
    fileSources: {},
  }),
  computed: {
    ...mapGetters({
      showModal: 'uploadReferences/showModal',
      awaitingUpload: 'uploadReferences/awaitingUpload',
      filesInStore: 'uploadReferences/modalFilesToUpload',
      areUploadsInProgress:
        'uploadReferences/areModalUploadsInProgress',
      errors: 'upload/errors',
      stillProcessing:
        'uploadReferences/areModalUploadsStillProcessing',
      watchingReferenceFiles:
        'uploadReferences/modalWatchingReferenceFiles',
      showProgressToast: 'uploadReferences/showProgressToast',
      toastUploadingCount:
        'uploadReferences/progressToastUploadingCount',
      toastProcessingCount:
        'uploadReferences/progressToastProcessingCount',
      toastCompleteCount:
        'uploadReferences/progressToastCompleteCount',
      toastWatchingUploadGroups:
        'uploadReferences/progressToastWatchingUploadGroups',
    }),
    ModalState() {
      return ModalState;
    },
    awaitingFileSelection() {
      return this.modalState === ModalState.AWAITING_FILE_SELECTION;
    },
    awaitingSubmission() {
      return this.modalState === ModalState.AWAITING_SUBMISSION;
    },
    processing() {
      return this.modalState === ModalState.PROCESSING;
    },
    complete() {
      return [
        ModalState.COMPLETE,
        ModalState.COMPLETE_WITH_DUPLICATES,
      ].includes(this.modalState);
    },
    completeWithDuplicates() {
      return this.modalState === ModalState.COMPLETE_WITH_DUPLICATES;
    },
    modalState() {
      if (
        !this.isWatchingFilesProcess &&
        !this.areUploadsInProgress
      ) {
        return this.files.length === 0
          ? ModalState.AWAITING_FILE_SELECTION
          : ModalState.AWAITING_SUBMISSION;
      } else if (this.areUploadsInProgress || this.stillProcessing) {
        return ModalState.PROCESSING;
      } else {
        return this.potentialDuplicatesDetected
          ? ModalState.COMPLETE_WITH_DUPLICATES
          : ModalState.COMPLETE;
      }
    },
    toastModel: {
      get: function () {
        return this.showProgressToast;
      },
      set: function (value) {
        if (!value) {
          this.dismissToast();
        }
      },
    },
    files: {
      get: function () {
        return this.filesInStore;
      },
      set: function (files) {
        this.setFilesInStore(files);
      },
    },
    referenceFileSelectorModel: {
      get: function () {
        return this.filesInStore.map(({ file }) => file);
      },
      set: function (files) {
        this.files = files.map((file) => ({
          id:
            this.files.find((upload) => upload.file === file)?.id ||
            createUUID(),
          file,
        }));
      },
    },
    uploadingFiles() {
      return this.files.filter(
        (file) =>
          file.progress < 1 || !(file.uri in this.uriToReferenceFile)
      );
    },
    uriToReferenceFile() {
      return this.watchingReferenceFiles.reduce((acc, upload) => {
        if (upload.uri in acc) {
          return acc;
        }
        acc[upload.uri] = upload;
        return acc;
      }, {});
    },
    referenceFiles() {
      const uploadedByCurrentSession = this.files
        .filter((file) => file.uri in this.uriToReferenceFile)
        .map((file) => this.uriToReferenceFile[file.uri]);
      const sessionUploadsById = uploadedByCurrentSession.reduce(
        (acc, upload) => {
          acc[upload.id] = upload;
          return acc;
        },
        {}
      );
      const uploadedByOtherSessions = this.watchingReferenceFiles.filter(
        (upload) => !(upload.id in sessionUploadsById)
      );
      return [
        ...uploadedByCurrentSession,
        ...uploadedByOtherSessions,
      ];
    },
    totalNewReferences() {
      return this.referenceFiles.reduce(
        (count, referenceFile) =>
          count + referenceFile.newReferenceCount,
        0
      );
    },
    referenceFileSelectorClasses() {
      return {
        ['with-many-files']: this.files.length >= 5,
      };
    },
    allUploadsFailed() {
      return (
        this.files.length > 0 &&
        this.files.every((file) => file.id in this.errors)
      );
    },
    maxWidth() {
      return this.files.length > 0 || this.referenceFiles.length > 0
        ? 570
        : 450;
    },
    isWatchingFilesProcess() {
      return this.watchingReferenceFiles.length > 0;
    },
    potentialDuplicatesDetected() {
      return this.watchingReferenceFiles.some(
        (referenceFile) => referenceFile.potentialDuplicateCount > 0
      );
    },
  },
  methods: {
    ...mapActions({
      setFilesInStore: 'uploadReferences/setModalFilesToUpload',
      beginUpload: 'uploadReferences/beginUpload',
      cancelUploads: 'upload/cancelUploads',
      cancelUpload: 'uploadReferences/cancelUpload',
      openModal: 'uploadReferences/openModal',
      closeModal: 'uploadReferences/closeModal',
      dismissToast: 'uploadReferences/dismissProgressToast',
    }),
    onSetLabel(file, label) {
      const upload = this.files.find(
        (upload) => upload.file === file
      );
      const id = upload?.id;
      if (id) Vue.set(this.fileLabels, id, label);
    },
    onSetSource(file, source) {
      const upload = this.files.find(
        (upload) => upload.file === file
      );
      const id = upload?.id;
      if (id) Vue.set(this.fileSources, id, source);
    },
    onUpload() {
      this.beginUpload({
        reviewId: this.review.id,
        fileIdToPreScreenedOutcome: this.fileLabels,
        fileIdToSource: this.fileSources,
      });
    },
    onResolveDuplicates() {
      this.closeModal();
      this.$emit('resolve-duplicates');
    },
    onClose() {
      this.closeModal();
    },
    onViewProgress() {
      this.openModal({
        watchUploadGroups: this.toastWatchingUploadGroups,
      });
    },
  },
};
</script>

<style type="scss" scoped>
.file-selector {
  min-height: 120px;
}
.file-list.with-many-files {
  height: 250px;
}
.line-height-150 {
  line-height: 150% !important;
}
</style>
